import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiFlagFill, PiUserLight, PiBriefcaseLight, PiFileLight } from 'react-icons/pi';
import Badge from './Badge';
import { BadgeItem } from './types/badgeTypes';
import CollaboratorStack from '../access/CollaboratorStack';

type Collaborator = {
  UserID: string;
  Email: string;
  FullName: string;
  ProfilePicture?: string;
  Initials?: string;
};

type ListItemProps = {
  title: string;
  subtitle?: string;
  details?: { label: string; value: string }[];
  badges?: BadgeItem[];
  link?: string;
  onClick?: () => void;
  collaborators?: Collaborator[];
  currentUserId?: string;
  icon?: React.ReactNode;
  style?: React.CSSProperties;
  processingStatus?: string;
  processingCompletedAt?: string;
};

const ListItem: React.FC<ListItemProps> = ({
  title,
  subtitle,
  details = [],
  badges = [],
  link = '',
  onClick,
  collaborators = [],
  currentUserId,
  icon,
  style,
  processingStatus,
  processingCompletedAt,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const handleClick = () => {
    onClick ? onClick() : history.push(link);
  };

  const isProcessing = processingStatus === 'running' || processingStatus === 'processing';
  const statusBadge = badges.find(badge => ['Active', 'Completed', 'Draft', 'Overdue', 'Closed', 'EngagementOngoing'].includes(badge.label));
  const priorityBadge = badges.find(badge => ['High', 'Medium', 'Low'].includes(badge.label));
  const otherBadges = badges.filter(badge => !['Active', 'Completed', 'Draft', 'Overdue', 'Closed', 'EngagementOngoing', 'High', 'Medium', 'Low'].includes(badge.label));

  const filteredCollaborators = collaborators.filter(collab => collab.UserID !== currentUserId);

  return (
    <div
      onClick={handleClick}
      className="glass relative cursor-pointer rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-3"
      style={style}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          {icon || <PiBriefcaseLight className="text-gray-500 dark:text-gray-400" size={18} />}
          {isProcessing ? (
            <div className="h-5 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse"></div>
          ) : (
            <h3 className="text-md text-gray-900 dark:text-white">{t(title)}</h3>
          )}
        </div>
        {statusBadge && (
          <Badge color={statusBadge.color}>
            {t(statusBadge.label === 'EngagementOngoing' ? 'Active' : statusBadge.label)}
          </Badge>
        )}
      </div>

      {isProcessing ? (
        <div className="space-y-2">
          <div className="h-3.5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse"></div>
          <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse"></div>
        </div>
      ) : (
        <>
          {subtitle && (
            <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
              {subtitle}
            </div>
          )}
        </>
      )}

      {priorityBadge && (
        <div className="flex items-center gap-2">
          <PiFlagFill className={`text-${priorityBadge.color}-500`} />
          <span className="text-sm text-gray-700 dark:text-gray-300">{t(priorityBadge.label)}</span>
        </div>
      )}

      {otherBadges.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {otherBadges.map((badge, index) => (
            <Badge key={index} color={badge.color} tooltip={badge.tooltip} icon={badge.icon}>
              {t(badge.label)}
            </Badge>
          ))}
        </div>
      )}

      {details && details.length > 0 && (
        <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-gray-200 dark:border-gray-700">
          {details.filter(detail => !['Documents', 'Client', 'Collaborators', 'Access'].includes(detail.label)).map((detail, index) => (
            <div key={index}>
              <div className="text-gray-500 dark:text-gray-400">{t(detail.label)}</div>
              <div className="text-gray-700 dark:text-gray-300 truncate">{t(detail.value)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          {details.find(detail => detail.label === 'Documents') && (
            <div className="flex items-center gap-1">
              <PiFileLight />
              <span>{details.find(detail => detail.label === 'Documents')!.value}</span>
            </div>
          )}
        </div>

        {filteredCollaborators.length > 0 && (
          // Raw rows: `CollaboratorStack` reads the picture under any spelling,
          // where this read `ProfilePicture` alone.
          <CollaboratorStack
            collaborators={filteredCollaborators}
            max={filteredCollaborators.length}
            size="md"
          />
        )}
      </div>
    </div>
  );
};

export default ListItem;
